export interface CampaignPlanning {
  description: string | null;
  serviceOffer: string | null;
  targetNewCustomers: number | null;
  targetRevenueAmount: number | null;
  budgetAmount: number | null;
  startsOn: string | null;
  endsOn: string | null;
}

export interface Campaign {
  id: number;
  name: string;
  status: string;
  serviceLines: string[];
  planning: CampaignPlanning | null;
  updatedAt: string;
}

export interface CampaignWorkflow {
  completedSteps: boolean[];
  currentStep: number;
  targetCount: number;
  arrangedCount: number;
  unavailableCount: number;
  closed: boolean;
  closeStatus: 'Completed' | 'Cancelled' | null;
  stageCounts?: { key: string; label: string; count: number }[];
  actionCounts?: { key: string; label: string; count: number }[];
  followUpDates?: { dueAt: string | null; count: number }[];
}

/** Campaign results from saved data: won = member Leads that reached a won stage after joining. */
export interface CampaignProgress {
  targets: number;
  contacted: number;
  quoted: number;
  won: number;
  wonRevenue: number;
  weeks: CampaignProgressWeek[];
}

/** One Monday-based week of logged contact and won Leads. */
export interface CampaignProgressWeek {
  weekStart: string;
  calls: number;
  visits: number;
  emails: number;
  meetings: number;
  won: number;
  wonRevenue: number;
}

/** A campaign Lead's owner, next action and due date; omitted fields stay unchanged. */
export interface LeadPlanPatch {
  salesRepUserId?: number;
  nextActionType?: string;
  nextActionDue?: string;
}

export interface CampaignLeadPlanItem extends LeadPlanPatch {
  leadId: number;
}

export interface CampaignLeadPlansUpdated {
  updated: number[];
  skipped: number[];
}

/** An active target; coordinates are null when its Lead has no mapped Place. */
export interface CampaignMemberLocation {
  leadId: number;
  placeId: number | null;
  lat: number | null;
  lng: number | null;
}

export interface CampaignMembersChange {
  add: number[];
  remove: number[];
}

export interface CampaignMembersChanged {
  added: number[];
  removed: number[];
  unchanged: number;
}

export interface SaveCampaign {
  name: string;
  serviceLines: string[];
  planning: CampaignPlanning;
}

// One editor and one API for all waste service lines.
export const campaignServiceLines = [
  { value: 'Residential', label: 'Residential', hint: 'Households, communities, cart sizes and collection frequency.' },
  { value: 'Commercial', label: 'Commercial', hint: 'Business types, waste streams, container sizes and collection frequency.' },
  { value: 'RollOff', label: 'Roll-off', hint: 'Jobsites, contractors, container sizes, rental periods and weight allowances.' },
] as const;

export const campaignStatuses = [
  'Draft', 'AwaitingData', 'Ready', 'Discovering', 'Researching', 'ReviewReady',
  'Active', 'Completed', 'Paused', 'Cancelled', 'BlockedData',
];

export function campaignStatusLabel(status: string): string {
  return status.replace(/([a-z])([A-Z])/g, '$1 $2');
}

export function campaignServicesLabel(lines: readonly string[]): string {
  return lines.map(value => campaignServiceLines.find(line => line.value === value)?.label ?? value).join(', ');
}
